/**
 * BulkDeleteDialog — type-to-confirm gate for permanently deleting
 * archived contacts/companies (pattern copied from the canonical
 * auth/components/DeleteHistoryDialog).
 *
 * This is the one irreversible action in the Leads arche, and the
 * records are SHARED Vault data — deleting here also removes them from
 * Fias 360 — so a plain "Are you sure?" is not enough: the destructive
 * button stays disabled until the user types the confirmation word.
 */

import { useState, type FormEvent } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BulkDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** How many records the delete will target. */
  count: number;
  /** Singular / plural nouns for copy: "contact" / "contacts". */
  noun: string;
  nounPlural: string;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

const CONFIRMATION_WORD = 'delete';

export function BulkDeleteDialog({
  open,
  onOpenChange,
  count,
  noun,
  nounPlural,
  onConfirm,
  isDeleting,
}: BulkDeleteDialogProps) {
  const [confirmationInput, setConfirmationInput] = useState('');
  const isConfirmationValid = confirmationInput.toLowerCase() === CONFIRMATION_WORD;
  const label = count === 1 ? noun : nounPlural;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isConfirmationValid || isDeleting) return;
    try {
      await onConfirm();
      setConfirmationInput('');
    } catch {
      // Error surfacing is the parent's job (toast); catching here only
      // prevents an unhandled rejection.
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) setConfirmationInput('');
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">
            Permanently delete {count} {label}?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              These records are shared across Fias — deleting them here also removes them from Fias
              360, along with their activity history.
            </p>
            <p>
              {label.charAt(0).toUpperCase() + label.slice(1)} linked to deals or managed in Fias
              360 will be skipped and reported.
            </p>
            <p className="font-semibold text-destructive">This action cannot be undone.</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirm-bulk-delete">
              Type <span className="font-mono font-bold">{CONFIRMATION_WORD}</span> to confirm
            </Label>
            <Input
              id="confirm-bulk-delete"
              type="text"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={`Type "${CONFIRMATION_WORD}" to confirm`}
              disabled={isDeleting}
              autoComplete="off"
              autoFocus
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              type="submit"
              variant="destructive"
              disabled={!isConfirmationValid || isDeleting}
            >
              {isDeleting ? 'Deleting…' : `Delete ${count} ${label}`}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
